import { Mediator } from 'mediator-js';
import log, { cleanObjectProperties } from 'Utilities/log';

/**
 * When you want to import this, do so as:
 * import * as mediator from 'Utility/mediator'
 * so you can do mediator.subscribe() or mediator.publish() for example
 *
 * https://github.com/ajacksified/Mediator.js
 */

const _m = new Mediator();

const logEvent = (...args: unknown[]) => {
  const [id, ...rest] = args;

  if (log.getLogLevel() !== 'DEBUG') {
    log.groupAndTrace(id);

    return;
  }

  const cleanedArgs = rest.map((v) => {
    if (typeof v === 'object') {
      return cleanObjectProperties(v);
    }

    return v;
  });

  log.groupAndTrace(id, ...cleanedArgs);
};

const subscribe = _m.subscribe.bind(_m);
const once = _m.once.bind(_m);
const remove = _m.remove.bind(_m);

const publish = <T extends [string, unknown] | [string]>(...args: T) => {
  // want to know who published the event
  logEvent(...args);

  const [id, ...originalArguments] = args;

  try {
    _m.publish(id, ...originalArguments);
  } catch (error) {
    log.error(`Failed to publish event ${id}`, error);
  }
};

const publishAndWait = (id: string, ...args: unknown[]): void => {
  // want to know who published the event
  logEvent(id, ...args);

  _m.publish(id, ...args);
};

const get = (id: string) => {
  // want to know who published the event
  logEvent(id);

  const data = {};

  _m.publish(id, data);

  return data;
};

export type MediatorRequest<K = unknown> = {
  [key: string]: unknown;
  data?: K;
  auth?: { username: string; password: string };
  _topicId?: string;
};

type MediatorRequestWithResponse<T = unknown, K = unknown> = {
  [key: string]: unknown;
  data?: K;
  _topicId?: string;
  response?: Promise<T>;
};

// you can optionally supply typing automatically for certain mediator requests, here's a commented example
// export function request<T, K>(id: 'api:invoke', request?: MediatorRequest<K>): Promise<LVApiResponse<T>>;
function request<T, K = unknown>(
  id: string | MediatorRequest<K>,
  requestData?: MediatorRequest<K>
): Promise<T> {
  let req = requestData;

  if (typeof id === 'string') {
    if (req) {
      req._topicId = id;
    } else {
      req = { _topicId: id };
    }
  }

  req ??= {};

  if (!req._topicId) {
    throw new Error('missing request._topicId');
  }

  // want to know who published the event
  logEvent(req._topicId, request);

  const _request: MediatorRequestWithResponse<T, K> = req;

  return new Promise<T>((resolve, reject) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      _m.publish(_request._topicId!, _request);

      if (!_request.response) {
        reject(new Error('missing Response'));

        return;
      }

      _request.response
        .then((val) => {
          resolve(val);
        })
        .catch((ex: unknown) => {
          reject(ex);
        });
    } catch (ex) {
      reject(ex);
    }
  });
}

export default {
  get,
  once,
  publish,
  publishAndWait,
  remove,
  request,
  subscribe,
};
